<script setup lang="ts">
export interface ModalPdfPreviewProps {
    source?: string
}
export interface ModalPdfPreviewEmits {
    (e: 'close'): void
}

const props = withDefaults(
    defineProps<ModalPdfPreviewProps>(),
    {
        source: undefined,
    }
)

const { $t } = useNuxtApp()
const emit = defineEmits<ModalPdfPreviewEmits>()

const dearFlip = ref(null)
const isModalOpen = ref(false)
const pdfContainerRef = ref<HTMLElement>()

// Watch for source changes to control modal visibility
watch(() => props.source, (newSource) => {
    isModalOpen.value = Boolean(newSource)
}, { immediate: true })

// WCAG 2.4.3: Handle focus escape from DearFlip viewer
// DearFlip creates elements that may steal focus - we need to monitor and return it
let focusObserver: MutationObserver | null = null

const setupDearFlipFocusTrap = () => {
    if (!pdfContainerRef.value) return

    // Monitor for DearFlip creating elements
    focusObserver = new MutationObserver(() => {
        // Find any DearFlip elements added to body and make them accessible
        const dflipFullscreen = document.querySelector('body > .df-lightbox-wrapper, body > .df-container')
        if (dflipFullscreen && !dflipFullscreen.closest('#modal-wrap')) {
            // DearFlip created fullscreen element - need to handle it
            // Add aria attributes for accessibility
            dflipFullscreen.setAttribute('role', 'dialog')
            dflipFullscreen.setAttribute('aria-modal', 'true')
            dflipFullscreen.setAttribute('aria-label', $t('PDF prehliadač na celú obrazovku') as string)
        }

        // Enhance DearFlip toolbar buttons for keyboard accessibility
        enhanceDearFlipAccessibility()
    })

    focusObserver.observe(document.body, {
        childList: true,
        subtree: false
    })

    // Also observe the container for DearFlip rendering its controls
    if (pdfContainerRef.value) {
        focusObserver.observe(pdfContainerRef.value, {
            childList: true,
            subtree: true
        })
    }

    // Initial enhancement after a delay for DearFlip to render
    // Multiple attempts to catch all dynamically rendered elements
    setTimeout(enhanceDearFlipAccessibility, 500)
    setTimeout(enhanceDearFlipAccessibility, 1000)
    setTimeout(enhanceDearFlipAccessibility, 2000)
}

/**
 * Enhance DearFlip elements for keyboard accessibility
 * DearFlip buttons may have tabindex="-1" or lack proper ARIA labels
 */
const enhanceDearFlipAccessibility = () => {
    // Remove aria-hidden from DearFlip container so buttons can receive focus
    const dfElements = document.querySelectorAll('.df-element[aria-hidden="true"], ._df_book, .df-container')
    dfElements.forEach((el) => {
        el.removeAttribute('aria-hidden')
    })

    // Find ALL clickable elements in DearFlip toolbar - use broad selectors
    // DearFlip uses various class patterns: df-ui-*, _df_button, etc.
    const allDflipClickables = document.querySelectorAll(`
        [class*="df-ui-"] ,
        [class*="_df_button"],
        [class*="df-container"] button,
        [class*="df-container"] [onclick],
        [class*="df-container"] a,
        .df-ui-controls *[class*="btn"],
        .df-ui-controls *[class*="icon"],
        ._df_thumb-container,
        ._df_zoom-wrapper button,
        ._df_controls button,
        ._df_controls > div
    `)

    allDflipClickables.forEach((el) => {
        const htmlEl = el as HTMLElement

        // Skip non-interactive elements
        if (htmlEl.tagName === 'SVG' || htmlEl.tagName === 'PATH' || htmlEl.tagName === 'SPAN') {
            return
        }

        // Make it focusable if it has a click handler or looks interactive
        const hasClick = htmlEl.hasAttribute('onclick') || htmlEl.style.cursor === 'pointer'
        const isButton = htmlEl.tagName === 'BUTTON' || htmlEl.classList.toString().includes('btn')
        const isLink = htmlEl.tagName === 'A'

        if (hasClick || isButton || isLink || htmlEl.classList.toString().includes('icon')) {
            // Remove tabindex="-1" or add tabindex="0"
            if (htmlEl.getAttribute('tabindex') === '-1' || !htmlEl.hasAttribute('tabindex')) {
                if (htmlEl.tagName !== 'BUTTON' && htmlEl.tagName !== 'A') {
                    htmlEl.setAttribute('tabindex', '0')
                } else if (htmlEl.getAttribute('tabindex') === '-1') {
                    htmlEl.removeAttribute('tabindex')
                }
            }

            // Add role if missing
            if (!htmlEl.hasAttribute('role') && htmlEl.tagName !== 'BUTTON' && htmlEl.tagName !== 'A') {
                htmlEl.setAttribute('role', 'button')
            }
        }
    })

    // Specifically target the bottom toolbar icons
    const toolbarIcons = document.querySelectorAll('._df_book-main ._df_controls > div, .df-ui-wrapper > div')
    toolbarIcons.forEach((icon) => {
        const htmlIcon = icon as HTMLElement
        if (htmlIcon.children.length > 0 || htmlIcon.querySelector('svg')) {
            htmlIcon.setAttribute('tabindex', '0')
            htmlIcon.setAttribute('role', 'button')
        }
    })
}

const cleanupDearFlipFocusTrap = () => {
    if (focusObserver) {
        focusObserver.disconnect()
        focusObserver = null
    }
}

watch(isModalOpen, (isOpen) => {
    if (isOpen) {
        nextTick(() => {
            setupDearFlipFocusTrap()
        })
    } else {
        cleanupDearFlipFocusTrap()
    }
})

onUnmounted(() => {
    cleanupDearFlipFocusTrap()
})

const flipbookOptions = computed(() => ({
    backgroundColor: '#ffffff',
    viewerType: 'flipbook',
    is3D: true,
    source: props.source,
    text: {
        toggleSound: $t('Zapnúť/Vypnúť zvuk'),
        toggleThumbnails: $t('Zobraziť/Skryť miniatúry'),
        toggleOutline: $t('Zobraziť/Skryť obsah/záložky'),
        previousPage: $t('Predchádzajúca strana'),
        nextPage: $t('Nasledujúca strana'),
        toggleFullscreen: $t('Prepnúť na celú obrazovku'),
        zoomIn: $t('Priblížiť'),
        zoomOut: $t('Oddialiť'),
        resetZoom: $t('Resetovať priblíženie'),
        pageFit: $t('Prispôsobiť strane'),
        widthFit: $t('Prispôsobiť šírke'),
        toggleHelp: $t('Zobraziť pomocníka'),
        search: $t('Hľadať v PDF'),
        singlePageMode: $t('Režim jednej strany'),
        doublePageMode: $t('Režim dvoch strán'),
        downloadPDFFile: $t('Stiahnuť PDF súbor'),
        gotoFirstPage: $t('Ísť na prvú stranu'),
        gotoLastPage: $t('Ísť na poslednú stranu'),
        print: $t('Tlačiť'),
        play: $t('Spustiť automatické prehrávanie'),
        pause: $t('Pozastaviť automatické prehrávanie'),
        share: $t('Zdieľať'),
        close: $t('Zavrieť'),
        mailSubject: $t('Pozri si tento FlipBook'),
        mailBody: $t('Pozri si túto stránku {{url}}'),
        loading: $t('Načítavam'),
        thumbTitle: $t('Miniatúry'),
        outlineTitle: $t('Obsah'),
        searchTitle: $t('Vyhľadávanie'),
        searchPlaceHolder: $t('Hľadať'),
        analyticsEventCategory: $t('DearFlip'),
        analyticsViewerReady: $t('Dokument pripravený'),
        analyticsViewerOpen: $t('Dokument otvorený'),
        analyticsViewerClose: $t('Dokument zatvorený'),
        analyticsFirstPageChange: $t('Zmenená prvá strana'),
    }
    ,
}))

const handleClose = () => {
    isModalOpen.value = false
    emit('close')
}
</script>

<template>
  <Modal
    :is-open="isModalOpen"
    size="fullscreen"
    @close="handleClose"
  >
    <div class="px-5 px-md-12 h-100 d-flex flex-column">
      <!-- Accessible toolbar with download option -->
      <div
        class="pdf-toolbar d-flex justify-content-end align-items-center py-2 gap-3"
        role="toolbar"
        :aria-label="$t('Nástroje PDF dokumentu')"
      >
        <a
          v-if="props.source"
          :href="props.source"
          target="_blank"
          rel="noopener"
          class="btn btn-secondary btn-small"
          :aria-label="$t('Stiahnuť alebo otvoriť PDF v novom okne')"
        >
          <span aria-hidden="true">📥</span>
          {{ $t('Stiahnuť PDF') }}
        </a>
      </div>

      <!-- Flipbook viewer -->
      <div
        ref="pdfContainerRef"
        class="flex-grow-1 position-relative"
      >
        <DearFlipViewer
          v-if="isModalOpen && props.source"
          ref="dearFlip"
          :options="flipbookOptions"
          :show-accessible-alternative="false"
          :aria-label="$t('Interaktívny prehliadač PDF')"
        />
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.pdf-toolbar {
  flex-shrink: 0;
  border-bottom: 1px solid var(--global-color-black-10, #e0e0e0);
  margin-bottom: 0.5rem;
}
</style>
